"use client";

/* eslint-disable @next/next/no-img-element -- static Figma vectors at their exact sizes */

import React, { useRef, useState } from "react";
import { GROQImage } from "@/sanity/lib/definitions";
import { Trademania2Video } from "@/sanity/pages/PageTrademania2Template";
import { PageTrademania2ReplaySchema } from "@/sanity/pages/PageTrademania2ReplayTemplate";
import { Chip, Highlight } from "@/components/trademania-2/shared";
import { Emphasis } from "@/components/trademania-2-replay/shared";

import lines from "@/public/trademania-2-replay/hero-lines.svg";
import linesPhone from "@/public/trademania-2-replay/hero-lines-m.svg";
import playIcon from "@/public/trademania-2-replay/play.svg";

/**
 * The replay: loops muted (as the launch page hero) until the play button restarts it with
 * sound and the native controls.
 */
function ReplayVideo({ video, poster, label }: { video?: Trademania2Video; poster?: GROQImage; label?: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  const play = () => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = false;
    el.currentTime = 0;
    setPlaying(true);
    void el.play();
  };

  return (
    <div className="relative aspect-[398/224] w-full overflow-clip rounded-[6px] border border-[#0B0C0D]/10 bg-black lg:aspect-[1024/576] lg:rounded-[12px]">
      {video?.url && (
        <video
          ref={videoRef}
          className="absolute inset-0 h-full w-full object-cover"
          src={video.url}
          poster={poster?.src}
          autoPlay
          muted
          loop={!playing}
          controls={playing}
          playsInline
          preload="auto"
        />
      )}
      {!playing && video?.url && (
        // Figma 248 × 66 pill, 0.72 of it on phones
        <button
          type="button"
          onClick={play}
          className="group absolute top-1/2 left-1/2 w-[31.034rem] -translate-x-1/2 -translate-y-1/2 scale-[.72] rounded-[80px] border border-white p-[.5rem] lg:scale-100">
          <span className="flex w-full items-center justify-center gap-[1.43rem] rounded-[51.08px] border-[.762px] border-white/5 bg-white py-[2.152rem] pr-[2.92rem] pl-[2.767rem] transition-colors duration-300 group-hover:bg-[#F4F4ED]">
            <span className="relative h-[2.583rem] w-[2.583rem] shrink-0 -scale-y-100 -rotate-90">
              <img
                src={playIcon.src}
                alt=""
                width={17.898}
                height={15.5}
                className="absolute top-0 right-[6.7%] bottom-1/4 left-[6.7%] block h-[75%] w-[86.6%] max-w-none"
              />
            </span>
            <span className="font-geist text-[2.5rem] leading-normal whitespace-nowrap text-black">{label}</span>
          </span>
        </button>
      )}
    </div>
  );
}

export default function Hero({ data }: { data: PageTrademania2ReplaySchema }) {
  return (
    <section className="relative overflow-clip pb-[4rem] lg:mx-[6px] lg:rounded-t-[8px] lg:pb-[12rem]">
      {/* sized in % of the hero width (Figma 467.8 / 430 and 2071 / 1908), not rem: rem stops
          following the viewport on tablets and above 1920, which left the lines short of the edge */}
      <img
        src={linesPhone.src}
        alt=""
        aria-hidden
        className="pointer-events-none absolute top-[-1.578rem] left-[-4.337%] h-auto w-[108.79%] max-w-none lg:hidden"
      />
      <img
        src={lines.src}
        alt=""
        aria-hidden
        className="pointer-events-none absolute top-[-2.99rem] left-[-6.761%] hidden h-auto w-[108.543%] max-w-none lg:block"
      />

      <div className="relative z-[1] mx-auto flex w-full flex-col items-center gap-[3rem] px-[2rem] pt-[12.625rem] pb-[.75rem] lg:w-[128rem] lg:gap-[4rem] lg:px-0 lg:pt-[16.5rem] lg:pb-0">
        <div className="flex w-full flex-col items-center gap-[2rem] lg:gap-[2.5rem]">
          <Chip
            text={data?.heroChip}
            slashes
            className="backdrop-blur-[5.35px]"
          />
          <div className="flex w-full flex-col items-center gap-[1.75rem] lg:gap-[3.25rem]">
            <h1 className="w-full text-center font-owners text-[4.5rem] leading-[.94] font-medium tracking-[.01em] whitespace-pre-line text-[#0B0C0D] uppercase lg:text-[8rem]">
              <Emphasis
                text={data?.heroTitle}
                emphasis={(part) => (
                  <Highlight
                    className="italic"
                    boxClassName="top-[-.056em] bottom-[-.032em] border-x lg:top-[-.0625em] lg:bottom-0">
                    {part}
                  </Highlight>
                )}
              />
            </h1>
            {data?.heroText && (
              <p className="text-center font-geist text-[2rem] leading-[1.5] font-medium whitespace-pre-wrap text-[#0B0C0D]/80 lg:text-[2.5rem]">
                <span
                  aria-hidden
                  className="text-[#62992B]/80">
                  {" ↓  "}
                </span>
                {data.heroText}
                <span
                  aria-hidden
                  className="text-[#62992B]">
                  {"  ↓"}
                </span>
              </p>
            )}
          </div>
        </div>
        <ReplayVideo
          video={data?.heroVideo}
          poster={data?.heroPoster}
          label={data?.heroPlayLabel}
        />
      </div>
    </section>
  );
}
